/**
 * Value-pattern recognizers — a reusable library of per-value predicates used
 * by type inference and by value-evidence column matching (which must be able
 * to OVERRIDE a misleading header, e.g. a "Region" column of 2-letter state
 * codes → State). US-first per M3 scope. All predicates take a raw cell string
 * and are tolerant of surrounding whitespace.
 */

const ZIP_RE = /^\d{5}(-\d{4})?$/;

// USPS 2-letter codes: 50 states + DC + the five inhabited territories.
export const US_STATE_CODES: ReadonlySet<string> = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "GU", "VI", "AS", "MP",
]);

const BOOLEAN_TRUE = new Set(["true", "yes", "y", "t", "1"]);
const BOOLEAN_FALSE = new Set(["false", "no", "n", "f", "0"]);

// Common date shapes: ISO (YYYY-MM-DD), US (M/D/YYYY), and dotted — conservative
// on purpose so a bare integer is never mistaken for a date.
const DATE_RES = [
  /^\d{4}-\d{1,2}-\d{1,2}([ T]\d{1,2}:\d{2}(:\d{2})?)?$/,
  /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
  /^\d{1,2}\.\d{1,2}\.\d{2,4}$/,
];

export function isZip(v: string): boolean {
  return ZIP_RE.test(v.trim());
}

export function isStateCode(v: string): boolean {
  return US_STATE_CODES.has(v.trim().toUpperCase());
}

export function isNumeric(v: string): boolean {
  const t = v.trim();
  if (t === "") return false;
  return Number.isFinite(Number(t.replace(/,/g, "")));
}

export function isLatValue(v: string): boolean {
  const n = Number(v.trim());
  return Number.isFinite(n) && n >= -90 && n <= 90;
}

// A decimal-degree coordinate: signed decimal with >= 3 fractional digits
// (GPS/geocoder output has many; prices/quantities have 0-2), within lng range.
// The decimal-precision floor is what keeps a 2-decimal price column out of the
// "coordinate" type bucket.
const COORD_RE = /^-?\d{1,3}\.\d{3,}$/;
export function isCoordinateLike(v: string): boolean {
  const t = v.trim();
  if (!COORD_RE.test(t)) return false;
  const n = Number(t);
  return n >= -180 && n <= 180;
}

export function isLngValue(v: string): boolean {
  const n = Number(v.trim());
  return Number.isFinite(n) && n >= -180 && n <= 180;
}

// Country names/codes we recognize by VALUE (US-first + common neighbors). Used
// to corroborate a `country` role so the near-homograph "County" — which has no
// country-like values — can't be mistaken for "Country" on header spelling alone.
const COUNTRY_TOKENS: ReadonlySet<string> = new Set([
  "us", "usa", "u s a", "united states", "united states of america", "america",
  "ca", "can", "canada", "mx", "mex", "mexico", "gb", "uk", "united kingdom",
  "au", "aus", "australia", "de", "germany", "fr", "france",
]);
export function isCountryLike(v: string): boolean {
  return COUNTRY_TOKENS.has(v.trim().toLowerCase());
}

export function isBooleanLike(v: string): boolean {
  const t = v.trim().toLowerCase();
  return BOOLEAN_TRUE.has(t) || BOOLEAN_FALSE.has(t);
}

export function isDateLike(v: string): boolean {
  return DATE_RES.some((re) => re.test(v.trim()));
}

/** Fraction of non-empty samples that satisfy a predicate (drives confidence). */
export function matchRatio(values: string[], predicate: (v: string) => boolean): number {
  const nonEmpty = values.filter((v) => v.trim() !== "");
  if (nonEmpty.length === 0) return 0;
  return nonEmpty.filter(predicate).length / nonEmpty.length;
}
